import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import xLogoDark from "@/assets/x-logo-dark.webp";

export function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden bg-[#050510]">
      {/* Mobile-only simple gradient (replaces heavy blobs) */}
      <div
        className="absolute inset-x-0 bottom-0 h-[55%] md:hidden pointer-events-none"
        style={{
          zIndex: 1,
          background: "radial-gradient(ellipse at 50% 90%, hsl(261 72% 64% / 0.35), transparent 65%)",
          filter: "blur(40px)",
        }}
      />

      {/* Aurora glow effect - desktop only, keyframes in index.css */}
      <div className="absolute inset-x-0 bottom-0 h-[70%] pointer-events-none hidden md:block" style={{ zIndex: 1 }}>
        <div
          className="absolute rounded-full"
          style={{
            width: "700px", height: "500px",
            background: "radial-gradient(ellipse, hsl(200 95% 60% / 0.6), hsl(210 90% 50% / 0.25), transparent 70%)",
            bottom: "5%", left: "10%", filter: "blur(100px)",
            animation: "aurora-pulse-1 12s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "600px", height: "450px",
            background: "radial-gradient(ellipse, hsl(270 90% 65% / 0.65), hsl(280 85% 55% / 0.3), transparent 70%)",
            bottom: "0%", left: "30%", filter: "blur(90px)",
            animation: "aurora-pulse-2 15s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "550px", height: "400px",
            background: "radial-gradient(ellipse, hsl(320 85% 65% / 0.6), hsl(330 80% 55% / 0.25), transparent 70%)",
            bottom: "10%", left: "50%", filter: "blur(80px)",
            animation: "aurora-pulse-3 10s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "500px", height: "380px",
            background: "radial-gradient(ellipse, hsl(230 95% 55% / 0.55), hsl(240 90% 45% / 0.2), transparent 70%)",
            bottom: "15%", left: "65%", filter: "blur(75px)",
            animation: "aurora-pulse-4 18s ease-in-out infinite",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "450px", height: "350px",
            background: "radial-gradient(ellipse, hsl(185 90% 55% / 0.45), transparent 65%)",
            bottom: "20%", left: "20%", filter: "blur(70px)",
            animation: "aurora-pulse-5 14s ease-in-out infinite",
          }}
        />
      </div>

      {/* Dark X Logo - decorative, fade in */}
      <div 
        className="absolute inset-x-0 flex justify-center pointer-events-none bottom-[-15%] md:bottom-[-60%] animate-[hero-fade-in_1.8s_ease-out_0.3s_both]"
        style={{ zIndex: 5 }}
      >
        <img
          src={xLogoDark}
          alt=""
          width={900}
          height={440}
          loading="lazy"
          decoding="async"
          className="w-[420px] sm:w-[500px] md:w-[1100px] lg:w-[1500px] xl:w-[1900px] select-none"
          style={{
            filter: "brightness(0) saturate(100%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, black 40%, black 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 40%, black 100%)",
          }}
        />
      </div>

      {/* Hero entrance keyframes */}
      <style>{`
        @keyframes hero-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes hero-fade-down {
          from { opacity: 0; transform: translateY(-24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-fade-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Main Content */}
      <div className="relative pt-32 md:pt-40 pb-24" style={{ zIndex: 10 }}>
        <div className="container max-w-5xl mx-auto px-6">
          <div className="text-center">

            {/* Main headline — fade down */}
            <h1
              className="text-[2.25rem] sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light text-white mb-5 leading-[1.1] tracking-tight animate-[hero-fade-down_0.8s_ease-out_0.1s_both]"
            >
              <span className="md:hidden">
                {t("hero.titleMobileLine1")}<br />
                {t("hero.titleMobileLine2")} <span className="gradient-text-animated font-normal">{t("hero.titleMobileSubLine")}</span>
              </span>
              <span className="hidden md:inline">
                {t("hero.title")}<br />
                <span className="gradient-text-animated font-normal animate-[hero-fade-up_0.8s_ease-out_0.35s_both] inline-block">{t("hero.titleSub")}</span>
              </span>
            </h1>

            {/* Subheadline — fade up staggered */}
            <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed px-4 sm:px-0 animate-[hero-fade-up_0.7s_ease-out_0.45s_both]">
              {t("hero.subtitle")}
            </p>

            {/* CTAs — fade up staggered */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4 px-4 sm:px-0 animate-[hero-fade-up_0.7s_ease-out_0.6s_both]">
              <Link
                to="/platform/voice-ai"
                className="w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium rounded-full bg-white text-black hover:bg-white/90 transition-all"
              >
                {t("hero.exploreVoiceAI")}
              </Link>
              <Link
                to="/schedule"
                className="w-full sm:w-auto inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium rounded-full bg-transparent border border-white/20 text-white hover:bg-white/5 hover:border-white/30 transition-all"
              >
                {t("hero.scheduleCall")}
              </Link>
            </div>
          </div>
        </div>
        
        {/* Spacer for X logo visual */}
        <div className="h-16 md:h-24" />
      </div>
    </section>
  );
}
